import { useState } from "react";
import { Copy, KeyRound, Mail, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, Loading, PageHeader, Spinner } from "@/components/States";
import { copy, inviteLink } from "@/components/project/SettingsTab";
import ContactsBook from "@/components/workspace/ContactsBook";

const ROLES = ["admin", "crew", "client"];

function InviteForm({ onDone }) {
  const [f, setF] = useState({ email: "", name: "", role: "crew" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/invites", f);
      if (data.existing_user) toast.message("That person already has an account");
      else {
        if (data.emailed) toast.success(`Invitation emailed to ${data.email}`); else copy(inviteLink(data.token));
        setF({ email: "", name: "", role: f.role });
      }
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="grid gap-2 rounded-xl border bg-card p-5 sm:grid-cols-[1fr_1fr_140px_auto]" data-testid="team-invite-form">
      <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-invite-email" />
      <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-invite-name" />
      <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
        <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize" data-testid={`team-invite-role-${r}`}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <Button type="submit" disabled={busy} className="rounded-full" data-testid="team-invite-submit">{busy ? <Spinner /> : <UserPlus className="mr-1 h-4 w-4" />}Invite</Button>
    </form>
  );
}

export default function Team() {
  const { user: me } = useAuth();
  const users = useFetch("/users");
  const invites = useFetch("/invites");
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}/role`, { role }); toast.success("Role updated"); users.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetLink = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-link`);
      copy(`${window.location.origin}/reset-password?token=${data.token}`);
      if (data.emailed) toast.success(`Reset email sent to ${u.email}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async (i) => {
    try { await api.post(`/invites/${i.id}/resend`); toast.success(`Invitation re-sent to ${i.email}`); invites.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (i) => {
    try { await api.delete(`/invites/${i.id}`); invites.reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-5xl space-y-10 p-6 md:p-10 lg:p-12">
      <PageHeader eyebrow="Team, invites and roles" title="People" />
      <section className="space-y-3">
        <p className="text-sm text-muted-foreground">Invitations are emailed from "{me.workspace?.branding?.email_sender_name || me.workspace?.name}" and set the person's role. Roles can only be changed here, by an admin.</p>
        <InviteForm onDone={invites.reload} />
      </section>
      <section className="space-y-3">
        <h2 className="section-title">Pending invites</h2>
        {invites.loading ? <Loading rows={1} /> : invites.error ? <ErrorState message={invites.error} onRetry={invites.reload} /> : invites.data.length === 0 ? <p className="text-sm text-muted-foreground">No pending invites.</p> : (
          <div className="divide-y rounded-xl border bg-card" data-testid="pending-invites">
            {invites.data.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="flex-1 truncate">{i.email} <Badge variant="outline" className="ml-1 capitalize">{i.role}</Badge> {i.emailed ? <Badge variant="secondary" className="ml-1" data-testid={`invite-emailed-${i.id}`}>Emailed</Badge> : <Badge variant="destructive" className="ml-1" data-testid={`invite-not-emailed-${i.id}`}>Not emailed</Badge>}</span>
                <Button size="sm" variant="ghost" onClick={() => resend(i)} data-testid={`resend-invite-${i.id}`}><Mail className="mr-1 h-4 w-4" />Resend</Button>
                <Button size="sm" variant="ghost" onClick={() => copy(inviteLink(i.token))} data-testid={`copy-invite-${i.id}`}><Copy className="mr-1 h-4 w-4" />Copy link</Button>
                <Button size="icon" variant="ghost" aria-label="Revoke" onClick={() => revoke(i)} data-testid={`revoke-invite-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="space-y-3">
        <h2 className="section-title">Accounts</h2>
        {users.loading ? <Loading rows={2} /> : users.error ? <ErrorState message={users.error} onRetry={users.reload} /> : (
          <div className="divide-y rounded-xl border bg-card" data-testid="users-list">
            {users.data.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1"><p className="truncate font-medium">{u.name}</p><p className="truncate text-xs text-muted-foreground">{u.email}</p></div>
                <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === me.id}>
                  <SelectTrigger className="w-28 capitalize" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => resetLink(u)} data-testid={`reset-link-${u.id}`}><KeyRound className="mr-1 h-4 w-4" />Reset link</Button>
              </div>
            ))}
          </div>
        )}
      </section>
      <ContactsBook />
    </div>
  );
}
